import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

// Shared palette. Kept here rather than per-component so a series called
// "Booked" is the same green wherever it appears on the page.
export const PALETTE = {
  blue:   '#2563eb',
  green:  '#10b981',
  red:    '#ef4444',
  amber:  '#f59e0b',
  violet: '#8b5cf6',
  cyan:   '#06b6d4',
  pink:   '#ec4899',
  slate:  '#64748b',
}

export const SERIES_COLORS = [
  PALETTE.blue, PALETTE.green, PALETTE.violet, PALETTE.amber,
  PALETTE.cyan, PALETTE.pink, PALETTE.red, PALETTE.slate,
]

export function hexToRgba(hex, alpha) {
  const clean = String(hex).replace('#', '')
  if (clean.length !== 6) return `rgba(37, 99, 235, ${alpha})`
  const n = parseInt(clean, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

// The global Highcharts theme (utils/highchartsTheme.js) is light only: grid
// lines at #f3f4f6 and axis labels at #6b7280 disappear against #0d1117. Charts
// are canvas-adjacent SVG, so no `dark:` class can reach inside them — the
// colours have to be passed in, and passed in again when the theme changes.
function themeTokens(dark) {
  return dark
    ? { grid: 'rgba(255,255,255,0.06)', line: 'rgba(255,255,255,0.12)', text: '#8b949e', strong: '#e6edf3', card: '#1C2128' }
    : { grid: '#f3f4f6', line: '#e5e7eb', text: '#6b7280', strong: '#111827', card: '#ffffff' }
}

/**
 * Chart lifecycle for the analytics panels.
 *
 * Handles the three things every chart on this page was previously getting
 * slightly wrong on its own: rebuilding when the data changes, rebuilding when
 * the theme changes, and destroying the instance on unmount (without which
 * every visit to the tab leaked one).
 *
 * @param {Function} build  (el, tokens) => Highcharts options
 * @param {Array}    deps   reactive sources that should trigger a rebuild
 */
export function useAnalyticsChart(build, deps = []) {
  const el = ref(null)
  const isDark = ref(document.documentElement.classList.contains('dark'))
  let chart = null
  let observer = null

  function render() {
    if (!el.value) return
    // Highcharts arrives from a CDN via layouts/_highcharts.html.erb. If that
    // is blocked or still in flight, leave the area empty rather than throwing
    // and taking the rest of the dashboard down with it.
    if (typeof Highcharts === 'undefined') return

    destroy()
    const options = build(el.value, themeTokens(isDark.value))
    if (!options) return
    chart = options.__map
      ? Highcharts.mapChart(el.value, options)
      : Highcharts.chart(el.value, options)
  }

  function destroy() {
    if (chart) {
      chart.destroy()
      chart = null
    }
  }

  const schedule = () => nextTick(render)

  deps.forEach(source => watch(source, schedule, { deep: true }))
  watch(isDark, schedule)

  onMounted(() => {
    schedule()
    observer = new MutationObserver(() => {
      isDark.value = document.documentElement.classList.contains('dark')
    })
    observer.observe(document.documentElement, { attributeFilter: ['class'] })
  })

  onUnmounted(() => {
    observer?.disconnect()
    destroy()
  })

  return { el, isDark, render }
}

// Axis and tooltip defaults every chart here shares, so one panel cannot end
// up with a different tooltip to the panel beside it.
export function baseOptions(tokens) {
  return {
    chart: { backgroundColor: 'transparent', style: { fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif' } },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    xAxis: {
      gridLineColor: tokens.grid,
      lineColor: tokens.line,
      tickColor: tokens.line,
      labels: { style: { color: tokens.text, fontSize: '11px' } },
    },
    yAxis: {
      gridLineColor: tokens.grid,
      lineWidth: 0,
      title: { text: null },
      labels: { style: { color: tokens.text, fontSize: '11px' } },
    },
    tooltip: {
      backgroundColor: '#111827',
      borderColor: 'transparent',
      borderRadius: 10,
      shadow: false,
      padding: 10,
      style: { color: '#f9fafb', fontSize: '12px' },
      useHTML: true,
    },
  }
}
